import { useEffect, useCallback } from "react";
import { useParams, useMatch, useNavigate } from "react-router";
import { CompanyBody } from "@vviinn/user-managment-client-ts";

import { MandatoryCompanyId, getCompanyIdFromUrl } from "../../company";
import { getSiteIdFromUrl } from "../../site";
import { useAuth } from "../../auth";
import { useApi } from "../../api";
import {
  useAppDispatch,
  useAppSelector,
  getCompany,
  changeActiveSite,
} from "../../store";

export const useCompanies = () => {
  const { linkBeforeAuth, user, resetLinkBeforeAuth } = useAuth();
  const { getApi } = useApi();
  const dispatch = useAppDispatch();
  const { companyId } = useParams();
  const navigate = useNavigate();

  const isRootRoute = useMatch("/");
  const isCompanyState = !!useMatch("/company/:companyId");

  const sites = useAppSelector((state) => state.sites.sites);
  const companies = useAppSelector((state) => state.companies.companies);
  const companyState = useAppSelector((state) => state.company.status);
  const isUserAuthed = useAppSelector((state) => state.user.isAuth);
  const selectedCompanyId = useAppSelector(
    (state) => state.company.company?.id,
  );

  const updateCompany = useCallback(
    async (companyId: MandatoryCompanyId) => {
      if (!user) return;
      const api = await getApi();
      await dispatch(getCompany({ api, value: companyId }));
    },
    [companyState, user],
  );

  const getActualCompanyId = useCallback((): CompanyBody["id"] => {
    if (companyId) return companyId;
    return selectedCompanyId ?? companies[0]?.id;
  }, [companies, companyId, selectedCompanyId]);

  useEffect(() => {
    if (!isUserAuthed || companies.length === 0) return;

    let updateCompanyId: string = "";
    let navigationUrl: string = "";

    if (isRootRoute && linkBeforeAuth) {
      const companyId = getCompanyIdFromUrl(linkBeforeAuth);
      const siteId = getSiteIdFromUrl(linkBeforeAuth);

      if (companyId) {
        navigationUrl = linkBeforeAuth;
      } else if (siteId) {
        const site = sites.find((s) => s.id === siteId);
        if (site) changeActiveSite(site);
        navigationUrl = linkBeforeAuth;
      } else if (companies[0].id) {
        navigationUrl = linkBeforeAuth;
      }
      resetLinkBeforeAuth();
    } else if (isRootRoute && !linkBeforeAuth) {
      const targetCompanyId = getActualCompanyId();
      if (!targetCompanyId) return;

      navigationUrl = `/company/${targetCompanyId ?? companies[0].id}/site/${
        sites[0]?.id ?? "no-id"
      }/dashboard`;
    } else {
      const companyId = getActualCompanyId() ?? companies[0]?.id;
      const isCompanyInState = companies.find(
        (company) => company.id === companyId,
      );
      if (companyId && isCompanyInState) updateCompanyId = companyId;
    }

    if (updateCompanyId && !isCompanyState) updateCompany(updateCompanyId);
    if (navigationUrl) navigate(navigationUrl);
  }, [isRootRoute, companies]);
};
